/**
 * Custom Cart Drawer Component (Section Version)
 * Handles dynamic offer thresholds, progress bar, and cart operations.
 *
 * FIXES APPLIED:
 * 1. Double-add fix: handleGlobalSubmit now ONLY handles forms INSIDE the
 *    drawer (its own upsell buttons). Product forms elsewhere on the page are
 *    left to the theme, so a product is only ever added ONCE.
 * 2. A global fetch() wrapper detects when ANYTHING on the page adds/changes
 *    the cart (product page, cards, banners, sliders, theme components) and
 *    tells the drawer to refresh + open — using the theme's single request,
 *    with no duplicate add.
 * 3. Our own cart requests bypass that wrapper (via the raw fetch) so they
 *    never double-fire, and the cart icon updates live on every change.
 */

/* ============================================================
   GLOBAL CART-CHANGE DETECTOR (runs once, before the element)
   - Captures the original fetch as window.__ccdRawFetch.
   - Patches window.fetch so any /cart/add|change|update|clear made by the
     THEME dispatches an event the drawer listens to.
   - The drawer's OWN requests use window.__ccdRawFetch, so they bypass this
     and never get double-counted.
   ============================================================ */
(function () {
    if (window.__ccdFetchPatched) return;
    window.__ccdFetchPatched = true;

    const origFetch = window.fetch ? window.fetch.bind(window) : null;
    if (!origFetch) return;

    window.__ccdRawFetch = origFetch;

    window.fetch = function (...args) {
        let url = '';
        try {
            url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        } catch (e) { /* ignore */ }

        const isCartMutation = /\/cart\/(add|change|update|clear)/.test(url);
        const promise = origFetch(...args);

        if (isCartMutation) {
            promise.then(function (res) {
                if (res && res.ok) {
                    if (/\/cart\/add/.test(url)) {
                        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
                    } else {
                        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
                    }
                }
            }).catch(function () { /* ignore */ });
        }

        return promise;
    };
})();

class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.classes = {
            active: 'active',
            loading: 'is-loading'
        };
        this.removedVariantIds = new Set();
        this.lastUnlockedCount = 0;
        this.firstLoad = true;
        this._listenersBound = false; // guard for global listeners
    }

    /* Raw fetch that BYPASSES the global cart detector above.
       Used for all of the drawer's own cart requests so they don't get
       double-counted by the wrapper. */
    cf(url, opts) {
        const f = window.__ccdRawFetch || window.fetch;
        return f(url, opts);
    }

    connectedCallback() {
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleGlobalSubmit = this.handleGlobalSubmit.bind(this);
        this.changeQty = this.changeQty.bind(this);

        // Attach document-level listeners only ONCE per element lifetime.
        if (!this._listenersBound) {
            document.addEventListener('custom-cart:open', this.open);
            document.addEventListener('cart:item-added', () => { this.fetchCart(); this.open(); });
            document.addEventListener('cart:updated', () => this.fetchCart());
            document.addEventListener('submit', this.handleGlobalSubmit);
            this._listenersBound = true;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }

    disconnectedCallback() {
        document.removeEventListener('custom-cart:open', this.open);
        document.removeEventListener('submit', this.handleGlobalSubmit);
        this.removeEventListener('click', this.handleClick);
        this._listenersBound = false;
    }

    init() {
        this.drawer = this.querySelector('.ccd-drawer');
        this.overlay = this.querySelector('.ccd-overlay');
        this.progressBar = this.querySelector('.ccd-progress-fill');
        this.progressText = this.querySelector('.ccd-progress-text');
        this.itemsContainer = this.querySelector('.ccd-items-wrapper');
        this.milestoneLayer = this.querySelector('.ccd-milestones-layer');

        this.offers = this.getOffers();
        console.log('CCD: Offers loaded:', this.offers);
        if (this.offers.length > 0) {
            this.offers.sort((a, b) => a.threshold - b.threshold);
            this.renderMilestones();
        } else {
            setTimeout(() => {
                this.offers = this.getOffers();
                if (this.offers.length > 0) {
                    this.offers.sort((a, b) => a.threshold - b.threshold);
                    this.renderMilestones();
                    if (this.lastCart) this.updateProgress(this.lastCart);
                }
            }, 500);
        }

        this.removeEventListener('click', this.handleClick);
        this.addEventListener('click', this.handleClick);

        this.fetchCart();
        this.initCoupons();
        this.bindFooterEvents();
        this.initCartIconObserver();
    }

    bindFooterEvents() {
        const checkoutBtn = this.querySelector('.ccd-checkout-btn');
        if (checkoutBtn) {
            checkoutBtn.addEventListener('click', (e) => {
                const activeCode = sessionStorage.getItem('ccd_discount_code');
                if (activeCode) {
                    e.preventDefault();
                    window.location.href = `/checkout?discount=${activeCode}`;
                }
            });
        }
    }

    getOffers() {
        try {
            const script = this.querySelector('#ccd-offers-data');
            if (!script) return [];
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('CCD: Error parsing offers', e);
            return [];
        }
    }

    renderMilestones() {
        if (!this.milestoneLayer || !this.offers.length) return;
        this.milestoneLayer.innerHTML = '';

        const count = this.offers.length;
        const widthPerBlock = 100 / count;

        this.offers.forEach((offer, index) => {
            const percent = (index + 1) * widthPerBlock;
            const el = document.createElement('div');
            el.className = 'ccd-milestone';
            el.style.left = `${percent}%`;
            el.dataset.threshold = offer.threshold;

            const moneyVal = 'Rs. ' + (offer.threshold / 100).toLocaleString();

            let iconHtml = '';
            if (offer.icon && !offer.icon.includes('no-image')) {
                iconHtml = `<img src="${offer.icon}" class="icon-default" style="width:100%; height:100%; object-fit:contain; border-radius:50%;" alt="Reward">`;
            } else {
                iconHtml = `<svg class="icon-default" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`;
            }

            el.innerHTML = `
                <div class="ccd-milestone-label-top">${offer.rewardText}</div>
                <div class="ccd-milestone-icon">
                    ${iconHtml}
                    <svg class="icon-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" style="display:none;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div class="ccd-milestone-label-bottom">${moneyVal}</div>
            `;
            this.milestoneLayer.appendChild(el);
        });
    }

    handleClick(e) {
        if (e.target.closest('.ccd-close-btn') || e.target.closest('.ccd-overlay')) {
            this.close();
        }

        const removeBtn = e.target.closest('.ccd-remove-btn');
        if (removeBtn) {
            e.preventDefault();
            this.changeQty(removeBtn.dataset.line, 0);
        }
    }

    async handleGlobalSubmit(e) {
        // Must be an add-to-cart form...
        if (!(e.target.action && e.target.action.includes('/cart/add'))) return;

        // FIX (double-add): ONLY handle forms that live INSIDE the drawer
        // (the upsell "+" buttons / internal upsell). Product forms elsewhere
        // on the page are handled by the theme — we must NOT re-submit them.
        if (!this.contains(e.target)) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const form = e.target;

        // Per-form in-flight guard (blocks the same form firing twice in a tick)
        if (form.dataset.ccdSubmitting === 'true') return;
        form.dataset.ccdSubmitting = 'true';

        const submitBtn = form.querySelector('[type="submit"]');
        const originalHTML = submitBtn ? submitBtn.innerHTML : '';
        if (submitBtn) {
            submitBtn.setAttribute('disabled', 'true');
            if (!submitBtn.classList.contains('ccd-upsell-add-btn')) {
                submitBtn.innerText = 'Adding...';
            } else {
                submitBtn.innerHTML = '<svg class="ccd-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>';
            }
        }

        const formData = new FormData(form);
        try {
            // Use raw fetch so the global detector doesn't also react to it
            const res = await this.cf('/cart/add.js', {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                body: formData
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.description || 'Could not add item to cart');
            }
            await this.fetchCart(); // real-time refresh
            this.open();
        } catch (err) {
            console.error('CCD: Add to cart failed', err);
        } finally {
            if (submitBtn) {
                submitBtn.removeAttribute('disabled');
                submitBtn.innerHTML = originalHTML;
            }
            delete form.dataset.ccdSubmitting;
        }
    }

    open() {
        if (this.drawer) {
            this.classList.add(this.classes.active);
            document.body.style.overflow = 'hidden';
        } else {
            console.warn('CCD: Drawer not found, initializing...');
            this.init();
            if (this.querySelector('.ccd-drawer')) {
                this.classList.add(this.classes.active);
                document.body.style.overflow = 'hidden';
            }
        }
    }

    close() {
        this.classList.remove(this.classes.active);
        document.body.style.overflow = '';
    }

    async fetchCart() {
        try {
            const res = await this.cf('/cart.js');
            const cart = await res.json();
            this.lastCart = cart;

            try {
                const sectionUrl = `/?section_id=${this.dataset.sectionId}`;
                const sectionRes = await this.cf(sectionUrl);
                const sectionText = await sectionRes.text();

                const doc = new DOMParser().parseFromString(sectionText, 'text/html');

                const newItems = doc.querySelector('.ccd-items-wrapper');
                if (newItems && this.itemsContainer) {
                    this.itemsContainer.innerHTML = newItems.innerHTML;
                }

                const newFooter = doc.querySelector('.ccd-footer');
                const currentFooter = this.querySelector('.ccd-footer');
                if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                    this.bindFooterEvents();
                }

                const newUpsell = doc.querySelector('.ccd-upsell-sidebar');
                const currentUpsell = this.querySelector('.ccd-upsell-sidebar');
                if (newUpsell && currentUpsell) {
                    currentUpsell.innerHTML = newUpsell.innerHTML;
                }

                this.initCoupons();

            } catch (htmlErr) {
                console.warn('CCD: HTML fetch error', htmlErr);
            }

            this.updateProgress(cart);

        } catch (err) {
            console.error('CCD: FetchCart error', err);
        }
    }

    async changeQty(line, qty) {
        const rows = this.querySelectorAll('.ccd-item');
        if (rows[line - 1]) rows[line - 1].style.opacity = '0.5';

        if (qty === 0 && this.lastCart && this.lastCart.items[line - 1]) {
            const variantId = this.lastCart.items[line - 1].variant_id;
            this.removedVariantIds.add(variantId);
            console.log('CCD: User removed item', variantId);
        }

        try {
            await this.cf('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: line, quantity: qty })
            });
            this.fetchCart();
        } catch (e) {
            console.error('CCD: Qty change error', e);
            if (rows[line - 1]) rows[line - 1].style.opacity = '1';
        }
    }

    updateProgress(cart) {
        if (!cart) return;
        const totalPrice = cart.total_price;

        // Always keep the cart icon in sync (works even if no offers configured)
        this.updateCartIcon(cart);

        const title = this.querySelector('.ccd-title');
        if (title) {
            title.innerHTML = `Your Cart (<span id="ccd-count">${cart.item_count || 0}</span> item${cart.item_count === 1 ? '' : 's'})`;
        }

        if (!this.offers || this.offers.length === 0) return;
        const sortedOffers = this.offers;

        let percentage = 0;
        const segmentSize = 100 / sortedOffers.length;
        let prevThreshold = 0;

        for (let i = 0; i < sortedOffers.length; i++) {
            const currentThreshold = sortedOffers[i].threshold;

            if (totalPrice < currentThreshold) {
                const range = currentThreshold - prevThreshold;
                const spentInSegment = totalPrice - prevThreshold;
                let segmentProgress = (spentInSegment > 0 ? spentInSegment : 0) / range;
                if (segmentProgress < 0) segmentProgress = 0;
                percentage = (i * segmentSize) + (segmentProgress * segmentSize);
                break;
            } else {
                percentage = (i + 1) * segmentSize;
                prevThreshold = currentThreshold;
            }
        }

        if (totalPrice >= sortedOffers[sortedOffers.length - 1].threshold) {
            percentage = 100;
        }
        if (percentage > 100) percentage = 100;

        if (this.progressBar) {
            this.progressBar.style.width = `${percentage}%`;
        } else {
            this.progressBar = this.querySelector('.ccd-progress-fill');
            if (this.progressBar) this.progressBar.style.width = `${percentage}%`;
        }

        const milestones = this.querySelectorAll('.ccd-milestone');
        let currentUnlockedCount = 0;

        milestones.forEach(m => {
            const threshold = parseInt(m.dataset.threshold);
            const iconDefault = m.querySelector('.icon-default');
            const iconCheck = m.querySelector('.icon-check');

            if (totalPrice >= threshold) {
                m.classList.add('reached');
                if (iconDefault) iconDefault.style.display = 'none';
                if (iconCheck) iconCheck.style.display = 'block';
                currentUnlockedCount++;
            } else {
                m.classList.remove('reached');
                if (iconDefault) iconDefault.style.display = 'block';
                if (iconCheck) iconCheck.style.display = 'none';
            }
        });

        if (this.firstLoad) {
            this.lastUnlockedCount = currentUnlockedCount;
            this.firstLoad = false;
        } else {
            if (currentUnlockedCount > this.lastUnlockedCount) {
                this.fireConfetti();
            }
            this.lastUnlockedCount = currentUnlockedCount;
        }

        let nextOffer = sortedOffers.find(offer => offer.threshold > totalPrice);
        let message = '';
        if (nextOffer) {
            const amountRemaining = (nextOffer.threshold - totalPrice) / 100;
            const amountText = amountRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            message = `You are ₹${amountText} away from <strong>${nextOffer.rewardText}</strong>!`;
        } else {
            message = "🎉 You've unlocked all offers!";
        }
        if (this.progressText) this.progressText.innerHTML = message;

        this.manageFreeGifts(sortedOffers, cart);
    }

    /* Live-update the header cart count badge across common theme markups. */
        /* Live-update the header cart count badge. */
    updateCartIcon(cart) {
        const count = (cart && cart.item_count) || 0;

        // 1) Update #marvac-hd-cart-btn specifically (your header's cart button)
        const cartBtn = document.getElementById('marvac-hd-cart-btn');
        if (cartBtn) {
            // Look for any element that contains a number (the count)
            const walker = document.createTreeWalker(cartBtn, NodeFilter.SHOW_TEXT, null, false);
            let textNode;
            while (textNode = walker.nextNode()) {
                const text = textNode.textContent.trim();
                if (/^\d+$/.test(text)) {
                    textNode.textContent = count;
                    break;
                }
            }

            // Also look for common bubble/count elements inside the button
            const bubbleSelectors = [
                '.cart-count-bubble',
                '.cart-bubble', 
                '.header__cart-count',
                '[data-cart-count]',
                '.cart-count',
                '.count',
                '.quantity',
                '.bubble'
            ];
            
            bubbleSelectors.forEach(sel => {
                const bubble = cartBtn.querySelector(sel);
                if (bubble) {
                    const numEl = bubble.querySelector('[aria-hidden="true"]') || bubble;
                    if (numEl) numEl.textContent = count;
                    
                    if (count === 0) {
                        bubble.style.display = 'none';
                        bubble.setAttribute('hidden', '');
                    } else {
                        bubble.style.display = '';
                        bubble.removeAttribute('hidden');
                    }
                }
            });

            // Update aria-label
            const currentLabel = cartBtn.getAttribute('aria-label') || '';
            const newLabel = currentLabel.replace(/:\s*\d+/, `: ${count}`);
            if (currentLabel !== newLabel) {
                cartBtn.setAttribute('aria-label', newLabel);
            }
        }

        // 2) Fallback: generic selectors for other possible cart icons
        const selectors = [
            '#cart-icon-bubble', 
            '.cart-count-bubble', 
            '.cart-bubble', 
            '[data-cart-count]', 
            '.cart-count', 
            '.header__cart-count'
        ];
        selectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(el => {
                if (cartBtn && cartBtn.contains(el)) return;
                
                const numEl = el.querySelector('[aria-hidden="true"]');
                if (numEl) { 
                    numEl.textContent = count; 
                } else if (!el.querySelector('*')) { 
                    el.textContent = count; 
                }
                
                if (el.id === 'cart-icon-bubble' || el.classList.contains('cart-bubble') || el.classList.contains('cart-count-bubble')) {
                    el.style.display = count > 0 ? '' : 'none';
                }
            });
        });

        // 3) Dispatch event for other components
        document.dispatchEvent(new CustomEvent('cart:count-updated', { 
            detail: { count: count },
            bubbles: true 
        }));

        // 4) Refresh from server after a short delay
        setTimeout(() => this.refreshCartIconSection(), 300);
    }
      
            initCartIconObserver() {
        const observer = new MutationObserver(() => {
            const cartBtn = document.getElementById('marvac-hd-cart-btn');
            if (cartBtn && this.lastCart) {
                // Check if count text was reset by external re-render
                let hasCount = false;
                const walker = document.createTreeWalker(cartBtn, NodeFilter.SHOW_TEXT, null, false);
                let textNode;
                while (textNode = walker.nextNode()) {
                    if (/^\d+$/.test(textNode.textContent.trim())) {
                        hasCount = true;
                        break;
                    }
                }
                if (!hasCount) this.updateCartIcon(this.lastCart);
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }
    refreshCartIconSection() {
        const cartBtn = document.getElementById('marvac-hd-cart-btn')
            || document.querySelector('.action__cart')
            || document.querySelector('header a[href$="/cart"]')
            || document.querySelector('a[href$="/cart"]');
        if (!cartBtn) return;

        const sectionEl = cartBtn.closest('[id^="shopify-section-"]');
        if (!sectionEl) return;
        const sectionId = sectionEl.id.replace('shopify-section-', '');

        const rawFetch = window.__ccdRawFetch || window.fetch;
        rawFetch('/?section_id=' + encodeURIComponent(sectionId))
            .then(r => r.text())
            .then(html => {
                const doc = new DOMParser().parseFromString(html, 'text/html');

                let freshBtn = null;
                if (cartBtn.id) freshBtn = doc.getElementById(cartBtn.id);
                if (!freshBtn) freshBtn = doc.querySelector('.action__cart') || doc.querySelector('a[href$="/cart"]');

                if (freshBtn) {
                    const onclick = cartBtn.getAttribute('onclick');
                    cartBtn.innerHTML = freshBtn.innerHTML;
                    if (onclick) cartBtn.setAttribute('onclick', onclick);
                    
                    // Re-apply count in case section cache was stale
                    if (this.lastCart) this.updateCartIcon(this.lastCart);
                }
            })
            .catch(() => { /* ignore */ });
    }

    async manageFreeGifts(allOffers, cart) {
        if (!allOffers.length) return;
        const totalPrice = cart.total_price;
        const unlockedOffers = allOffers.filter(offer => offer.threshold <= totalPrice);
        const lockedOffers = allOffers.filter(offer => offer.threshold > totalPrice);

        let cleanUpNeeded = false;

        for (const offer of lockedOffers) {
            if (offer.type !== 'product' || !offer.variantId) continue;

            const lineItem = cart.items.find(item =>
                item.variant_id == offer.variantId &&
                item.properties && (item.properties['_FreeGift'] === 'true' || item.properties['_FreeGift'] === true)
            );

            if (lineItem) {
                console.log('CCD: Removing unqualified gift', offer.rewardText);
                try {
                    const lineIndex = cart.items.indexOf(lineItem) + 1;
                    await this.cf('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ line: lineIndex, quantity: 0 })
                    });
                    cleanUpNeeded = true;

                    this.removedVariantIds.delete(parseInt(offer.variantId));
                    this.removedVariantIds.delete(String(offer.variantId));

                } catch (e) { console.error('CCD: Gift removal failed', e); }
            }
        }

        if (cleanUpNeeded) {
            this.fetchCart();
            return;
        }

        for (const offer of unlockedOffers) {
            if (offer.type === 'product' && offer.variantId) {
                if (this.removedVariantIds.has(parseInt(offer.variantId)) || this.removedVariantIds.has(String(offer.variantId))) {
                    continue;
                }

                const inCart = cart.items.some(item => item.variant_id == offer.variantId);
                if (!inCart) {
                    console.log('CCD: Auto-adding free gift', offer.rewardText);
                    try {
                        await this.cf('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: offer.variantId,
                                quantity: 1,
                                properties: { '_FreeGift': 'true' }
                            })
                        });
                        this.fetchCart();
                        return;
                    } catch (e) { console.error('Auto Add Failed', e); }
                }
            }
        }
    }

    fireConfetti() {
        const container = document.createElement('div');
        container.className = 'ccd-confetti-container';
        container.style.cssText = 'position:absolute; top:0; left:0; width:100%; height:100%; pointer-events:none; z-index:999; overflow:hidden;';

        if (this.drawer) {
            this.drawer.appendChild(container);
        } else {
            this.appendChild(container);
        }

        for (let i = 0; i < 50; i++) {
            const p = document.createElement('div');
            p.className = 'ccd-confetti-piece';
            p.style.left = Math.random() * 100 + '%';
            p.style.animationDelay = (Math.random() * 2) + 's';
            p.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            p.style.width = (Math.random() * 8 + 5) + 'px';
            p.style.height = (Math.random() * 8 + 5) + 'px';
            container.appendChild(p);
        }

        setTimeout(() => { container.remove(); }, 5000);
    }

    initCoupons() {
        const input = this.querySelector('#ccd-coupon-input');
        const applyBtn = this.querySelector('#ccd-apply-coupon');
        const toggleBtn = this.querySelector('#ccd-toggle-coupons');
        const listContainer = this.querySelector('#ccd-coupon-list');
        const countLabel = this.querySelector('#ccd-coupon-count');

        let coupons = [];
        try {
            const script = this.querySelector('#ccd-coupons-data');
            if (script) coupons = JSON.parse(script.textContent);
            console.log('CCD: Coupons loaded:', coupons);
        } catch (e) { console.error('CCD: Coupon Parse Error', e); }

        if (countLabel) countLabel.innerText = `${coupons.length} coupons available`;

        if (listContainer) {
            listContainer.innerHTML = '';
            coupons.forEach(c => {
                const row = document.createElement('div');
                row.style.cssText = 'display:flex; justify-content:space-between; margin-bottom:8px; align-items:center; border-bottom:1px solid #eee; padding-bottom:5px;';
                row.innerHTML = `
                    <div>
                        <div style="font-weight:700; color:#000;">${c.code}</div>
                        <div style="font-size:11px; color:#555;">${c.description}</div>
                    </div>
                    <button class="ccd-use-coupon" data-code="${c.code}" style="background:#000; color:#fff; border:none; border-radius:4px; padding:4px 8px; font-size:10px; cursor:pointer;">APPLY</button>
                `;
                listContainer.appendChild(row);
            });

            if (coupons.length === 0 && toggleBtn) {
                toggleBtn.style.display = 'none';
                if (countLabel) countLabel.innerText = '';
            }
        }

        if (toggleBtn) {
            const newToggle = toggleBtn.cloneNode(true);
            toggleBtn.parentNode.replaceChild(newToggle, toggleBtn);
            newToggle.addEventListener('click', (e) => {
                e.preventDefault();
                const list = this.querySelector('#ccd-coupon-list');
                if (list) list.style.display = list.style.display === 'none' ? 'block' : 'none';
            });
        }

        // Bind the "use coupon from list" delegation only once
        if (!this._couponDelegateBound) {
            this._couponDelegateBound = true;
            this.addEventListener('click', (e) => {
                if (e.target.classList.contains('ccd-use-coupon')) {
                    e.preventDefault();
                    const code = e.target.dataset.code;
                    const inp = this.querySelector('#ccd-coupon-input');
                    if (inp) inp.value = code;
                    this.applyCoupon(code);
                }
            });
        }

        if (applyBtn) {
            const newApply = applyBtn.cloneNode(true);
            applyBtn.parentNode.replaceChild(newApply, applyBtn);
            newApply.addEventListener('click', (e) => {
                e.preventDefault();
                const inp = this.querySelector('#ccd-coupon-input');
                const code = inp ? inp.value.trim() : '';
                if (code) this.applyCoupon(code);
            });
        }

        const activeCode = sessionStorage.getItem('ccd_discount_code');
        if (activeCode && input) {
            input.value = activeCode;
            this.showCouponSuccess(activeCode);
        }
    }

    applyCoupon(code) {
        sessionStorage.setItem('ccd_discount_code', code);
        this.showCouponSuccess(code);
    }

    showCouponSuccess(code) {
        const message = this.querySelector('#ccd-coupon-message');
        const input = this.querySelector('#ccd-coupon-input');
        if (message) {
            message.style.display = 'block';
            message.style.color = '#00b894';
            message.innerHTML = `Discount code <strong>${code}</strong> applied! It will be calculated at checkout.`;
        }
        if (input) {
            input.disabled = true;
            input.style.color = '#00b894';
            input.style.fontWeight = '700';
        }
    }
}

customElements.define('custom-cart-drawer', CustomCartDrawer);

/* The header cart icon calls openCustomCartDrawer(); make sure it exists and
   simply opens this drawer. */
if (typeof window.openCustomCartDrawer !== 'function') {
    window.openCustomCartDrawer = function () {
        document.dispatchEvent(new CustomEvent('custom-cart:open', { bubbles: true }));
    };
}